/**
 * Generic authed JSON client for the Reeve .NET API.
 *
 * Every request carries the current bearer token (see auth.ts — the live Clerk
 * session token) plus Accept / Content-Type. A null token (no session) simply
 * omits Authorization and the request is left to 401.
 * On a non-2xx response the backend's ErrorResponse envelope
 * ({ error, code, details?, validationErrors? }) is parsed and its `.error`
 * message is thrown; if the body isn't a recognisable envelope we fall back to
 * the HTTP status text.
 *
 * Base URL comes from VITE_API_BASE_URL (trailing slash stripped). In dev it's
 * empty and Vite proxies "/api/*" to the backend (see vite.config.ts).
 *
 * The API is tenant-less on the surface: the server resolves the tenant from the
 * token, so nothing here ever references a tenant id.
 */

import { getAuthToken } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

/** The error envelope the backend returns on any non-2xx response. */
export interface ErrorResponse {
  error: string;
  code?: string;
  details?: unknown;
  validationErrors?: Record<string, string[]>;
}

/**
 * A failed API call, carrying enough to tell the two kinds of failure apart.
 *
 * The distinction matters because they mean opposite things to the user. A 404 or an empty list is
 * the backend ANSWERING: you have no company. No response at all, or a 5xx, means we do not know
 * what the user has. Treating the second as the first is how a dead backend ends up looking like a
 * brand-new account, which is alarming and wrong.
 */
export class ApiError extends Error {
  /** HTTP status, or null when no response came back at all (refused, DNS, offline, CORS). */
  readonly status: number | null;

  /** The request never got a usable answer: no response, or the server failed on its own side. */
  readonly isConnectivity: boolean;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.isConnectivity = status === null || status >= 500;
  }
}

/** True when this failure means "we could not reach Reeve", rather than a real answer. */
export function isConnectivityError(err: unknown): boolean {
  return err instanceof ApiError && err.isConnectivity;
}

async function throwFromResponse(response: Response, path: string): Promise<never> {
  let message = response.statusText || `Request to ${path} failed (${response.status})`;
  try {
    const body = (await response.json()) as Partial<ErrorResponse> | null;
    if (body && typeof body.error === 'string' && body.error.length > 0) {
      message = body.error;
    }
  } catch {
    // Body wasn't JSON / was empty — keep the status-text fallback.
  }
  throw new ApiError(message, response.status);
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const token = await getAuthToken();

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch (err) {
    // fetch only rejects when the request never completed: connection refused, DNS failure, offline,
    // CORS preflight rejection. A null status is how callers recognise "no answer at all".
    throw new ApiError(
      err instanceof Error && err.message ? err.message : `Could not reach ${path}`,
      null,
    );
  }

  if (!response.ok) {
    return throwFromResponse(response, path);
  }

  // 204 No Content (and other empty bodies) parse to undefined.
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}
